import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { PaymentDetailPage } from 'src/pages/payments/payment-detail/payment-detail';

import { RentCollectionService } from 'src/shared/services/rent-collection.service';

@Component({
  templateUrl: 'request-payment.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequestPaymentPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private featureGate = inject(FeatureGateService);
  private rentService = inject(RentCollectionService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  loaded = false;
  submitted = false;
  errorMessage = '';
  private panelClosedSubscription: Subscription;

  payerTypes = [
    { value: 'Tenant', viewValue: 'Tenant' },
    { value: 'Guest', viewValue: 'Guest' },
  ];

  form = this.fb.group({
    payer_name: ['', Validators.required],
    payer_type: ['Tenant', Validators.required],
    property_name: ['', Validators.required],
    unit: [''],
    description: ['Rent', Validators.required],
    amount: [null as number | null, [Validators.required, Validators.min(0.01)]],
    due_date: [new Date().toISOString().slice(0, 10), Validators.required],
  });

  createAction = () => this.create();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Request a Payment');
    }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  async create(): Promise<void> {
    this.submitted = true;
    this.cdr.markForCheck();
    if (!this.form.valid) { return; }
    if (this.featureGate.gate()) { return; }
    const v = this.form.getRawValue();
    const payment = await this.rentService.createPaymentRequest({
      payer_name: v.payer_name || '',
      payer_type: (v.payer_type as 'Tenant' | 'Guest') || 'Tenant',
      property_name: v.property_name || '',
      unit: v.unit || '',
      description: v.description || 'Payment',
      amount: Number(v.amount) || 0,
      due_date: String(v.due_date),
    });
    this.util.showSuccess('Payment request created. Copy the link to send it.');
    this.goToPage('payment-detail', { payment }, PaymentDetailPage);
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
